import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {Loader, Tooltip} from '@mantine/core';
import {css, CSSPropertiesComplete, StyleSheet} from 'aphrodite';
import React, {
  useState,
  KeyboardEvent,
  ChangeEvent,
  useRef,
  useCallback,
  useMemo,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
} from 'react';

import FolderSelector from 'file-browser/FolderSelector';
import {AgentType, ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {AgentToolbar} from 'llm/chat-library/AgentToolbar';
import * as ChatActions from 'llm/chat-library/ChatActions';
import {CitationGenerationMode, type CreateChatOptions} from 'llm/chat-library/ChatDAO';
import {
  AgentTypeError,
  ChatDraftSettings,
  ChatDraftStatus,
  getDraftState,
  setDraftState,
  removeFileFromDraft,
  setPublicDataEnabledPreference,
  useChatDraftState,
} from 'llm/chat-library/ChatDraftState';
import {ChatSelectedFile} from 'llm/chat-library/ChatLibraryTypes';
import CoScientistAgentWorkspaceFolderChip from 'llm/chat-library/CoScientistAgentWorkspaceFolderChip';
import {TargetItemInfo} from 'llm/chat-library/getTargetItem';
import type {CreateChatResult} from 'llm/chat-library/sidepanel/ChatRouter';
import {
  getAgentTypePlaceholderText,
  getChatTypesForNewAgentChat,
  getChatTypeForNewAgentChat,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import TargetItemChip from 'llm/chat-library/TargetItemChip';
import {
  AI_GRAY_200,
  AI_GRAY_425,
  CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX,
} from 'llm/chat-library/ui/AIStyleConstants';
import {useMCPEnabledServersOptional} from 'llm/mcp-client-library/MCPEnabledServersContext';
import useRealtimeVoiceSession from 'llm/realtime-voice/useRealtimeVoiceSession';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {BLUE_LIGHT, GRAY_DARKER, GRAY_LIGHTER} from 'ui-library/Colors';
import FileList from 'ui-library/FileList';
import FileRow from 'ui-library/FileRow';
import DropTarget from 'util/drag-and-drop/DropTarget';

export interface ChatInputProps {
  chatSessionId: string | null;
  agentTypeError?: AgentTypeError;
  inputText: string;
  setInputText: (message: string) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (enabled: boolean) => void;
  citationGenerationMode: CitationGenerationMode;
  setCitationGenerationMode: (mode: CitationGenerationMode) => void;
  onSubmit: () => void;
  /** When true, show a stop control instead of send while the assistant is responding. */
  showStopButton?: boolean;
  onInterrupt?: () => void;
  isInterrupting?: boolean;
  inputDisabled: boolean;
  /** All selected files (uploading, failed, completed) */
  selectedFiles: Array<ChatSelectedFile>;
  /** Called when user selects files to upload */
  onSelectFiles: (files: Array<File>) => void;
  /** Called when user removes a file (works for any status) */
  onRemoveFile: (key: string) => void;
  /**
   * The current agent type.
   * - Can be null while settings are still loading.
   * - When null, submission is blocked but the input is still usable.
   */
  agentType: AgentType | null;
  setAgentType: (agentType: AgentType | null) => void;
  allowedChatTypes: Array<ChatType>;
  hideAIWarning?: boolean;
  /** Target item for Compose-over-entries chats. Shows a chip indicator when present. */
  targetItem: TargetItemInfo | null;
  /** Persisted agent workspace folder ID for existing co-scientist chats. */
  agentWorkspaceFolderId?: string | null;
  setAgentWorkspaceFolderId?: (agentWorkspaceFolderId: string | null) => void;
  /** MCP server IDs selected for this chat. Undefined means "all enabled" (default). */
  selectedMcpServerIds?: Array<string> | undefined;
  setSelectedMcpServerIds?: (serverIds: Array<string> | undefined) => void;
  /** When true, styles the input text as muted (used for preview text from progressive disclosure). */
  isPreviewText?: boolean;
}

export interface NewChatInputHandle {
  submitWithText: (text: string) => Promise<void>;
  setPreviewText: (text: string) => void;
  clearPreviewText: () => void;
}

/**
 * Chat input for new chats. Uses draft state initialized by openDraftChat.
 */
export const NewChatInput = React.forwardRef<
  NewChatInputHandle,
  {
    onCreateChat: (result: CreateChatResult) => void;
    isPreviewText?: boolean;
    onHasUserInputChange?: (hasInput: boolean) => void;
  }
>(({onCreateChat, isPreviewText, onHasUserInputChange}, ref) => {
  const [agentWorkspaceFolderId, setAgentWorkspaceFolderId] = useState<string | null>(null);
  // Draft state is initialized by openDraftChat with agentType and publicDataEnabled
  const controls = useChatInputControls(null);
  const {inputText, agentType, hasUploadingFiles} = controls;

  useEffect(() => {
    if (!isPreviewText) {
      onHasUserInputChange?.(inputText.length > 0);
    }
  }, [inputText, isPreviewText, onHasUserInputChange]);

  const {isSubmitting, sendMessage} = useNewChat({
    onCreateChat,
    getCreateChatOptions: (selectedAgentType: AgentType) => {
      if (selectedAgentType === AgentType.CO_SCIENTIST && agentWorkspaceFolderId != null) {
        return {
          [AgentType.CO_SCIENTIST]: {
            agentWorkspaceFolderId,
          },
        };
      }
      return {};
    },
  });

  const handleSubmitMessage = useCallback(async () => {
    const isMissingAgentWorkspaceFolder =
      agentType === AgentType.CO_SCIENTIST && agentWorkspaceFolderId == null;
    if (
      !inputText.trim() ||
      isSubmitting ||
      !agentType ||
      hasUploadingFiles ||
      isMissingAgentWorkspaceFolder
    ) {
      return;
    }
    // Draft state already has everything - just submit
    await sendMessage();
  }, [agentType, agentWorkspaceFolderId, inputText, isSubmitting, hasUploadingFiles, sendMessage]);

  useImperativeHandle(
    ref,
    () => ({
      submitWithText: async (text: string) => {
        const draft = getDraftState(null);
        if (!draft || !draft.settings?.agentType) {
          return;
        }
        setDraftState(null, {
          ...draft,
          status: ChatDraftStatus.DRAFT,
          message: {...draft.message, text},
        });
        await sendMessage();
      },
      setPreviewText: (text: string) => {
        controls.setInputText(text);
      },
      clearPreviewText: () => {
        controls.setInputText('');
      },
    }),
    [sendMessage, controls]
  );

  return (
    <ChatInput
      chatSessionId={null}
      agentTypeError={controls.agentTypeError}
      {...controls}
      onSubmit={handleSubmitMessage}
      inputDisabled={isSubmitting}
      allowedChatTypes={getChatTypesForNewAgentChat()}
      hideAIWarning={true}
      agentWorkspaceFolderId={agentWorkspaceFolderId}
      setAgentWorkspaceFolderId={setAgentWorkspaceFolderId}
      targetItem={null}
      isPreviewText={isPreviewText}
    />
  );
});

export interface ChatInputControls {
  agentTypeError?: AgentTypeError;
  inputText: string;
  setInputText: (message: string) => void;
  /** All selected files (uploading, failed, completed) */
  selectedFiles: Array<ChatSelectedFile>;
  /** Whether any files are still uploading */
  hasUploadingFiles: boolean;
  /** Whether the draft message is pending (status is PENDING) */
  isPending: boolean;
  /** Called when user selects files to upload */
  onSelectFiles: (files: Array<File>) => void;
  /** Called when user removes a file (works for any status) */
  onRemoveFile: (key: string) => void;
  /**
   * The current agent type.
   * - Can be null while settings are still loading.
   * - For new chats, this comes from draft state initialized by openDraftChat.
   * - For existing chats, this comes from draft state seeded by useMessageThread.
   */
  agentType: AgentType | null;
  setAgentType: (agentType: AgentType | null) => void;
  publicDataEnabled: boolean;
  setPublicDataEnabled: (publicDataEnabled: boolean) => void;
  citationGenerationMode: CitationGenerationMode;
  setCitationGenerationMode: (citationGenerationMode: CitationGenerationMode) => void;
  /** MCP server IDs selected for this chat. Undefined means "all enabled" (default). */
  selectedMcpServerIds: Array<string> | undefined;
  setSelectedMcpServerIds: (serverIds: Array<string> | undefined) => void;
}

// TODO: Unify with useMessageThread.
function useNewChat({
  onCreateChat,
  getCreateChatOptions,
}: {
  onCreateChat: (result: CreateChatResult) => void;
  getCreateChatOptions?: (agentType: AgentType) => CreateChatOptions;
}): {
  isSubmitting: boolean;
  sendMessage: () => Promise<void>;
} {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const mcpEnabledServers = useMCPEnabledServersOptional();
  const mcpServerIds = mcpEnabledServers?.enabledServerIds;
  const {llmRouteCoScientistToUnified} = useRolloutFlags();
  const routeCoScientistToUnified = llmRouteCoScientistToUnified === true;

  const sendMessage = useCallback(async () => {
    // Read from draft state - it should already have everything from user input
    const draft = getDraftState(null);
    if (!draft) {
      return;
    }

    const agentType = draft.settings?.agentType;
    if (!agentType) {
      // This shouldn't happen - caller should check agentType before calling sendMessage
      return;
    }

    const chatType = getChatTypeForNewAgentChat(agentType);
    const createChatOptions = getCreateChatOptions?.(agentType) ?? {};

    setIsSubmitting(true);
    try {
      const {chatSessionId} = await ChatActions.createChatWithDraftMessage(chatType, {
        createChatOptions,
        createParams: {mcpServerIds, routeCoScientistToUnified},
      });
      onCreateChat({chatSessionId, chatType, initialAgentType: agentType});
    } finally {
      setIsSubmitting(false);
    }
  }, [getCreateChatOptions, onCreateChat, mcpServerIds, routeCoScientistToUnified]);

  return {isSubmitting, sendMessage};
}

/**
 * Custom hook for managing related input state.
 *
 * All draft state (text, files, settings) is persisted in global state via ChatDraftState.
 * This includes files being uploaded - they persist across component unmount/remount.
 *
 * For new chats: Draft state is initialized by openDraftChat with agentType and publicDataEnabled.
 * For existing chats: Draft state is seeded by useMessageThread from the loaded thread.
 *
 * @param chatId - The chat session ID, or null for new chats.
 */
export function useChatInputControls(chatId: string | null): ChatInputControls {
  // Draft state is the single source of truth for everything.
  // useChatDraftState always returns a draft state object (never null).
  // For new chats, it includes the default agent type so the input is immediately usable.
  const [draftState, setDraft] = useChatDraftState(chatId);

  // Derive values from draft state.
  const agentType = draftState.settings?.agentType ?? null;
  const publicDataEnabled = draftState.settings?.publicDataEnabled ?? false;
  const citationGenerationMode = draftState.settings?.citationGenerationMode ?? CitationGenerationMode.AUTO;
  const selectedMcpServerIds = draftState.settings?.selectedMcpServerIds;
  const agentTypeError = draftState.agentTypeError;
  // When status is PENDING, the message is being sent - show empty input
  const inputText = draftState.status === ChatDraftStatus.PENDING ? '' : draftState.message.text;
  const selectedFiles = useMemo(() => draftState.message.selectedFiles, [draftState.message.selectedFiles]);
  const hasUploadingFiles = selectedFiles.some((f) => f.completedUpload == null);

  // Helper to update the draft state.
  // This only updates fields that are explicitly provided - it does NOT fill in defaults.
  const updateDraft = useCallback(
    (updates: Partial<ChatDraftSettings & {text: string}>) => {
      // Don't overwrite PENDING status - sendMessage is handling this
      if (draftState.status === ChatDraftStatus.PENDING) {
        return;
      }

      // Determine the new settings.
      // - If settings was already defined, preserve it (with updated values)
      // - If settings was undefined and we're setting an agentType, create settings
      // - If settings was undefined and no agentType, keep undefined (still loading)
      const newAgentType = updates.agentType ?? draftState.settings?.agentType;
      const newPublicDataEnabled =
        updates.publicDataEnabled ?? draftState.settings?.publicDataEnabled ?? false;
      const newCitationGenerationMode =
        updates.citationGenerationMode ?? draftState.settings?.citationGenerationMode;
      // selectedMcpServerIds uses explicit undefined to mean "all enabled servers"
      const newSelectedMcpServerIds = Object.hasOwn(updates, 'selectedMcpServerIds')
        ? updates.selectedMcpServerIds
        : draftState.settings?.selectedMcpServerIds;
      const newSettings =
        newAgentType != null
          ? {
              agentType: newAgentType,
              publicDataEnabled: newPublicDataEnabled,
              citationGenerationMode: newCitationGenerationMode,
              selectedMcpServerIds: newSelectedMcpServerIds,
            }
          : undefined;

      setDraft({
        status: ChatDraftStatus.DRAFT,
        message: {
          text: updates.text ?? draftState.message.text,
          selectedFiles: draftState.message.selectedFiles,
        },
        settings: newSettings,
        agentTypeError:
          // Clear agentTypeError if the agent type has changed
          updates.agentType != null && updates.agentType !== draftState.settings?.agentType
            ? undefined
            : draftState.agentTypeError,
      });
    },
    [draftState, setDraft]
  );

  const setInputText = useCallback(
    (text: string) => {
      updateDraft({text});
    },
    [updateDraft]
  );

  const setAgentType = useCallback(
    (newAgentType: AgentType | null) => {
      if (newAgentType != null) {
        updateDraft({agentType: newAgentType});
      }
    },
    [updateDraft]
  );

  const setPublicDataEnabled = useCallback(
    (enabled: boolean) => {
      setPublicDataEnabledPreference(enabled);
      updateDraft({publicDataEnabled: enabled});
    },
    [updateDraft]
  );

  const setCitationGenerationMode = useCallback(
    (mode: CitationGenerationMode) => {
      updateDraft({citationGenerationMode: mode});
    },
    [updateDraft]
  );

  const setSelectedMcpServerIds = useCallback(
    (serverIds: Array<string> | undefined) => {
      updateDraft({selectedMcpServerIds: serverIds});
    },
    [updateDraft]
  );

  // File management - delegates to ChatActions/ChatDraftState
  const onSelectFiles = useCallback(
    (files: Array<File>) => {
      ChatActions.selectFilesForDraft(chatId, files);
    },
    [chatId]
  );

  const onRemoveFile = useCallback(
    (key: string) => {
      removeFileFromDraft(chatId, key);
    },
    [chatId]
  );

  const isPending = draftState?.status === ChatDraftStatus.PENDING;

  return {
    agentTypeError,
    inputText,
    setInputText,
    agentType,
    setAgentType,
    publicDataEnabled,
    setPublicDataEnabled,
    citationGenerationMode,
    setCitationGenerationMode,
    selectedMcpServerIds,
    setSelectedMcpServerIds,
    selectedFiles,
    hasUploadingFiles,
    isPending,
    onSelectFiles,
    onRemoveFile,
  };
}

export function ChatInput({
  chatSessionId,
  agentTypeError,
  inputText,
  setInputText,
  publicDataEnabled,
  setPublicDataEnabled,
  citationGenerationMode,
  setCitationGenerationMode,
  onSubmit,
  showStopButton = false,
  onInterrupt,
  isInterrupting = false,
  inputDisabled,
  selectedFiles,
  onSelectFiles,
  onRemoveFile,
  agentType,
  setAgentType,
  allowedChatTypes,
  hideAIWarning,
  agentWorkspaceFolderId,
  setAgentWorkspaceFolderId,
  targetItem,
  selectedMcpServerIds,
  setSelectedMcpServerIds,
  isPreviewText,
}: ChatInputProps): JSX.Element {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const realtimeVoiceSessionControls = useRealtimeVoiceSession(chatSessionId);

  // There may be inputText on initial mount if there was draft state.
  useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      const length = textarea.value.length;
      textarea.setSelectionRange(length, length);
    }
  }, []);

  const hasFiles = selectedFiles.length > 0;
  const hasUploadingFiles = selectedFiles.some((f) => f.completedUpload == null);
  const isNewCoScientistChat =
    chatSessionId == null && agentType === AgentType.CO_SCIENTIST && setAgentWorkspaceFolderId != null;
  const isExistingCoScientistChat =
    chatSessionId != null && agentType === AgentType.CO_SCIENTIST && agentWorkspaceFolderId != null;
  const hasRequiredAgentWorkspaceFolder = !isNewCoScientistChat || agentWorkspaceFolderId != null;
  const isSubmitDisabled =
    !showStopButton &&
    (inputDisabled ||
      (!inputText.trim() && !hasFiles) ||
      hasUploadingFiles ||
      agentType == null ||
      !hasRequiredAgentWorkspaceFolder);
  const isStopDisabled = isInterrupting || onInterrupt == null;
  const placeholderText =
    agentType != null
      ? getAgentTypePlaceholderText(agentType, targetItem, {isAgentStoppable: showStopButton})
      : 'Pick an agent to begin.';

  const handleMessageChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setInputText(e.target.value);
  };

  // Auto-resize the textarea to fit its content. Run as a layout effect keyed on inputText so the
  // height recalculates whenever the value changes, including when inputText is programmatically
  // cleared on submit (draft status -> PENDING). Without this, the previously-computed inline
  // height would persist, leaving a tall empty textarea that covers the message thread and
  // prevents the user from scrolling up. See BNCH bug report: "If my prompt is too long, I can't
  // scroll up anymore after hitting submit".
  useLayoutEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) {
      return;
    }
    if (isPreviewText) {
      textarea.style.height = '0px';
      return;
    }
    // Collapse first so `scrollHeight` reflects current content. Using only `height: auto` can
    // leave a stale large scrollHeight after deleting lines, which blocks the wrapper from shrinking.
    textarea.style.height = '0px';
    const scrollHeight = textarea.scrollHeight;
    const nextHeight = Math.max(72, Math.min(500, scrollHeight));
    textarea.style.height = `${nextHeight}px`;
  }, [inputText, isPreviewText]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>): void => {
      if (e.key === 'Enter' && !e.shiftKey) {
        // Enter (without shift) should submit the message if possible, and never type a newline.
        e.preventDefault();
        if (showStopButton) {
          if (!isStopDisabled) {
            onInterrupt?.();
          }
        } else if (!isSubmitDisabled) {
          onSubmit();
        }
      }
    },
    [isStopDisabled, isSubmitDisabled, onInterrupt, onSubmit, showStopButton]
  );

  const handleRemoveFile = useCallback(
    (file: File): void => {
      if (!inputDisabled) {
        const selectedFile = selectedFiles.find((f) => f.file === file);
        if (selectedFile) {
          onRemoveFile(selectedFile.key);
        }
      }
    },
    [inputDisabled, onRemoveFile, selectedFiles]
  );

  const handleSelectFiles = useCallback(
    (filesToSelect: Array<File>): void => {
      onSelectFiles(filesToSelect);
    },
    [onSelectFiles]
  );

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>): void => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleSelectFiles(Array.from(files));
      }
      // Reset the input value so the same file can be selected again
      e.target.value = '';
    },
    [handleSelectFiles]
  );

  const handleDrop = useCallback(
    (e: DragEvent): void => {
      e.preventDefault();
      e.stopPropagation();

      if (inputDisabled) {
        return;
      }

      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        handleSelectFiles(Array.from(files));
      }
    },
    [inputDisabled, handleSelectFiles]
  );

  const handleSelectSkill = useCallback(
    (skillName: string) => {
      const currentText = textareaRef.current?.value ?? '';
      const prefix = currentText.length > 0 && !currentText.endsWith(' ') ? ' ' : '';
      setInputText(`${currentText}${prefix}/${skillName} `);
      textareaRef.current?.focus();
    },
    [setInputText]
  );

  const submitControl = (
    <Button
      dataTestAttrs={{
        component: 'ChatInput',
        element: showStopButton ? 'stop' : 'submit',
      }}
      aria-label={showStopButton ? 'Stop' : 'Send'}
      buttonStyle={ButtonStyle.PRIMARY}
      size={ButtonSize.MINI}
      className={css(styles.chatToolbarMiniControl)}
      onClick={showStopButton ? onInterrupt : onSubmit}
      disabled={showStopButton ? isStopDisabled : isSubmitDisabled}
      icon={showStopButton ? 'stop' : 'arrow-up'}
    />
  );

  return (
    <div
      ref={inputContainerRef}
      className={css(styles.inputContainer)}
      {...getTestProps({component: 'ChatInput', element: 'root'})}
    >
      {agentTypeError && (
        <div className={css(styles.bannerWrapper)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={agentTypeError.title}
            supportingInfo={agentTypeError.detail}
          />
        </div>
      )}
      <DropTarget
        shouldCaptureDrop={true}
        mimeTypes={['Files']}
        dropEffect="copy"
        targetDesc="chat input"
        onDrop={handleDrop}
      >
        <div
          className={css(styles.inputGroup, inputDisabled && styles.inputGroupDisabled)}
          {...getTestProps({component: 'ChatInput', element: 'fileDropZone'})}
        >
          {isNewCoScientistChat && (
            <CoScientistAgentWorkspaceFolderPicker
              selectedAgentWorkspaceFolderId={agentWorkspaceFolderId ?? null}
              onChangeAgentWorkspaceFolderId={setAgentWorkspaceFolderId}
              inputDisabled={inputDisabled}
            />
          )}
          {isExistingCoScientistChat && (
            <CoScientistAgentWorkspaceFolderChip agentWorkspaceFolderId={agentWorkspaceFolderId} />
          )}
          {targetItem && <TargetItemChip targetItem={targetItem} />}
          <div className={css(styles.inputAreaWrapper)}>
            {!isPreviewText && inputText.length === 0 && (
              <div key={placeholderText} className={css(styles.placeholderOverlay)} aria-hidden={true}>
                {placeholderText}
              </div>
            )}
            {isPreviewText && (
              <div className={css(styles.previewTextOverlay)} aria-hidden={true}>
                {inputText}
              </div>
            )}
            <textarea
              ref={textareaRef}
              className={css(styles.input, isPreviewText && styles.inputPreview)}
              placeholder=""
              aria-placeholder={placeholderText}
              autoFocus={true}
              value={inputText}
              onChange={handleMessageChange}
              onKeyDown={handleKeyDown}
              disabled={inputDisabled}
            />
          </div>
          {hasFiles && (
            <div className={css(styles.fileListWrapper)}>
              <FileList>
                {selectedFiles.map((selectedFile) => (
                  <FileRow
                    key={selectedFile.key}
                    file={selectedFile.file}
                    onRequestRemove={handleRemoveFile}
                    additionalContent={!selectedFile.completedUpload && <Loader size="sm" />}
                  />
                ))}
              </FileList>
            </div>
          )}
          <div className={css(styles.inputGroupButtonRow)}>
            <div className={css(styles.inputGroupIconButtons)}>
              {/* Invisible input to trigger file picker */}
              <input
                {...getTestProps({component: 'ChatInput', element: 'fileInput'})}
                type="file"
                multiple={true}
                className="hidden"
                ref={fileInputRef}
                onChange={handleFileInputChange}
              />
              {/* Invisible input to trigger camera */}
              <input
                {...getTestProps({component: 'ChatInput', element: 'cameraInput'})}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                ref={cameraInputRef}
                onChange={handleFileInputChange}
              />
              <AgentToolbar
                chatSessionId={chatSessionId}
                textareaRef={textareaRef}
                fileInputRef={fileInputRef}
                cameraInputRef={cameraInputRef}
                allowedChatTypes={allowedChatTypes}
                agentType={agentType}
                setAgentType={setAgentType}
                inputDisabled={inputDisabled}
                publicDataEnabled={publicDataEnabled}
                setPublicDataEnabled={setPublicDataEnabled}
                citationGenerationMode={citationGenerationMode}
                setCitationGenerationMode={setCitationGenerationMode}
                selectedMcpServerIds={selectedMcpServerIds}
                setSelectedMcpServerIds={setSelectedMcpServerIds}
                realtimeVoiceSessionControls={realtimeVoiceSessionControls}
                onSelectSkill={handleSelectSkill}
              />
            </div>
            {showStopButton ? (
              <Tooltip label="Stop" openDelay={100} closeDelay={0} withinPortal={true}>
                <span>{submitControl}</span>
              </Tooltip>
            ) : (
              submitControl
            )}
          </div>
        </div>
      </DropTarget>
      {!hideAIWarning && (
        <div className={css(styles.warningMessage)}>AI can make mistakes. Always check results.</div>
      )}
    </div>
  );
}

function CoScientistAgentWorkspaceFolderPicker({
  selectedAgentWorkspaceFolderId,
  onChangeAgentWorkspaceFolderId,
  inputDisabled,
}: {
  selectedAgentWorkspaceFolderId: string | null;
  onChangeAgentWorkspaceFolderId: (agentWorkspaceFolderId: string | null) => void;
  inputDisabled: boolean;
}): JSX.Element {
  const handleAgentWorkspaceFolderChange = useCallback(
    (e: {target: {value: string | null}}): void => {
      onChangeAgentWorkspaceFolderId(e.target.value);
    },
    [onChangeAgentWorkspaceFolderId]
  );

  return (
    <div className={css(styles.coScientistAgentWorkspaceSection)}>
      <div className={css(styles.coScientistAgentWorkspaceFolderSelectorWrapper)}>
        <FolderSelector
          allowCreateFolder={true}
          enableRedesign={true}
          noFolderText="Select agent workspace folder"
          readOnly={inputDisabled}
          showInventory={true}
          showNotebook={true}
          unsetOnFetchFail={true}
          value={selectedAgentWorkspaceFolderId}
          onChange={handleAgentWorkspaceFolderChange}
          dataTestAttrs={{component: 'ChatInput', element: 'agentWorkspaceFolderPicker'}}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  inputContainer: {
    width: '100%',
    maxWidth: '800px',
    marginTop: '20px',
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    gap: '4px',
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    border: `1px solid ${AI_GRAY_200}`,
    borderRadius: '24px',
    backgroundColor: 'rgb(255, 255, 255)',
    transition: 'border-color 0.2s ease-in-out, box-shadow 0.25s ease-in-out',
    boxShadow: 'rgb(0 13 181 / 4%) 0px 0px 22px 0px',
    ':hover': {
      borderColor: Color.GRAY_300,
    },
    ':focus-within': {
      border: '1px solid rgba(90, 122, 236, 0.26)',
      boxShadow: 'rgb(0 13 181 / 5%) 0px 0px 30px 0px',
    },
  },
  inputGroupDisabled: {
    backgroundColor: GRAY_LIGHTER,
    ':hover': {
      borderColor: AI_GRAY_200,
    },
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    // Cap the textarea height so very long prompts don't push the rest of the input
    // (file list, toolbar, send button) off-screen or cover the message thread. Beyond
    // this cap, the textarea becomes internally scrollable via overflow-y: auto below.
    maxHeight: '500px',
    minHeight: 0,
    padding: '12px 16px',
    fontFamily: 'inherit',
    fontSize: '16px',
    lineHeight: '24px',
    border: 'none',
    background: 'transparent',
    resize: 'none',
    overflowY: 'auto',
    ':focus': {
      outline: 'none',
    },
  },
  inputAreaWrapper: {
    position: 'relative',
    width: '100%',
    minHeight: '72px',
  },
  placeholderOverlay: {
    position: 'absolute',
    left: '16px',
    right: '16px',
    top: '12px',
    pointerEvents: 'none',
    color: AI_GRAY_425,
    fontSize: '16px',
    fontFamily: 'inherit',
    lineHeight: '24px',
    overflow: 'hidden',
    animationName: [
      {
        from: {opacity: 0},
        to: {opacity: 1},
      },
    ],
    animationDuration: '450ms',
    animationTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)', // ease-in-out
  },
  inputPreview: {
    position: 'absolute',
    opacity: 0,
    pointerEvents: 'none',
    overflow: 'hidden',
  },
  previewTextOverlay: {
    padding: '12px 16px 0',
    maxHeight: '60px',
    fontSize: '16px',
    fontFamily: 'inherit',
    lineHeight: '24px',
    color: AI_GRAY_425,
    display: '-webkit-box' as CSSPropertiesComplete['display'],
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    wordBreak: 'break-word',
  },
  inputGroupButtonRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    padding: '10px 12px 12px 12px',
  },
  inputGroupIconButtons: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
  },
  /** Keeps the send control the same height as chat-toolbar `AIButton` / agent toggles (24px). */
  chatToolbarMiniControl: {
    boxSizing: 'border-box',
    minHeight: `${CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX}px`,
    height: `${CHAT_INPUT_TOOLBAR_CONTROL_HEIGHT_PX}px`,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '30px',
    borderRadius: '12px',
  },
  warningMessage: {
    fontSize: '12px',
    color: GRAY_DARKER,
    textAlign: 'center',
    marginTop: '4px',
    marginBottom: '8px',
  },
  bannerWrapper: {
    marginBottom: '8px',
  },
  fileListWrapper: {
    padding: '8px 12px',
    borderTop: `1px solid ${GRAY_LIGHTER}`,
    // Ugly hack: The global `table` class on FileList adds 17px margin-bottom. Offset that with a
    // negative margin so that this component isn't imposing its own margins and instead lets its
    // parent handle spacing.
    marginBottom: -17,
  },
  indicator: {
    backgroundColor: BLUE_LIGHT,
  },
  coScientistAgentWorkspaceSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  coScientistAgentWorkspaceFolderSelectorWrapper: {
    maxWidth: 260,
    padding: '12px 12px 0',
  },
});
